<ng-container *transloco="let t">
<!-- Projects Navigation Tree -->
<div class="mt-2 flex flex-col px-3 pb-4">
    @for (node of tree; track node.id) {
        <ng-container
            *ngTemplateOutlet="treeNode; context: { $implicit: node, siblings: tree }"
        ></ng-container>
    }

</div>

<!-- Recursive tree node template -->
<ng-template #treeNode let-node let-siblings="siblings">
    @if (node.type === 'route-link') {
        <!-- Route link (e.g. Account sub-pages) -->
        <button
            class="flex w-full items-center gap-2 rounded-md pr-2 py-1.5 text-sm text-gray-300 hover:bg-white/10 hover:text-white"
            [ngStyle]="{ 'padding-left': (8 + node.level * 20) + 'px' }"
            (click)="navigateToRoute(node.route)"
        >
            <mat-icon class="icon-size-4 text-gray-400" [svgIcon]="node.icon"></mat-icon>
            <span>{{ node.label }}</span>
        </button>
    } @else if (node.type === 'new-link') {
        <!-- "New X" link -->
        <button
            class="flex w-full items-center gap-2 rounded-md pr-2 py-1.5 text-sm text-gray-400 hover:bg-white/10 hover:text-white"
            [ngStyle]="{ 'padding-left': (8 + node.level * 20) + 'px' }"
            (click)="navigateToNew(node.id)"
        >
            <mat-icon class="icon-size-4 text-gray-500" [svgIcon]="node.icon"></mat-icon>
            <span>{{ node.label }}</span>
        </button>
    } @else if (node.type === 'project') {
        <!-- Project leaf node -->
        <button
            class="group flex w-full items-center rounded-md pr-2 py-1 text-sm text-gray-300 hover:bg-white/10 hover:text-white"
            [ngStyle]="{ 'padding-left': (8 + node.level * 20) + 'px' }"
            [matTooltip]="node.label"
            matTooltipPosition="right"
            [matTooltipShowDelay]="400"
            (click)="navigateToProject(node.project)"
        >
            <!-- Project type icon -->
            <mat-icon class="icon-size-4 mr-1.5 flex-shrink-0"
                [ngClass]="getTypeColor(node.project)"
                [svgIcon]="getTypeIcon(node.project)"
                [matTooltip]="getTypeTooltip(node.project)">
            </mat-icon>
            <!-- Project name -->
            <span class="truncate">{{ node.label }}</span>
            <!-- Spacer -->
            <span class="flex-auto"></span>
            <!-- Status icon on far right -->
            <mat-icon class="icon-size-4 ml-2 flex-shrink-0"
                [ngClass]="getStatusColor(node.project)"
                [class.animate-spin]="node.project.status === 'working' || node.project.status === 'in_progress'"
                [svgIcon]="getStatusIcon(node.project)"
                [matTooltip]="getStatusTooltip(node.project)">
            </mat-icon>
        </button>
    } @else {
        <!-- Folder node (client / product / top-level) -->
        <div class="flex flex-col">
            <!-- Folder header -->
            <div
                class="group flex w-full cursor-pointer items-center rounded-md pr-2 py-1.5 text-sm text-gray-200 hover:bg-white/10"
                [ngStyle]="{ 'padding-left': (8 + node.level * 20) + 'px' }"
                (click)="toggleNode(node, siblings)"
            >
                <!-- Expand/collapse arrow -->
                @if (node.children?.length) {
                    <mat-icon class="icon-size-3.5 mr-1 flex-shrink-0 text-gray-500 transition-transform"
                        [class.rotate-90]="node.expanded"
                        [svgIcon]="'heroicons_solid:chevron-right'">
                    </mat-icon>
                } @else {
                    <span class="mr-1 w-3.5"></span>
                }

                <!-- Folder icon -->
                <mat-icon class="icon-size-4 mr-1.5 flex-shrink-0 text-gray-400" [svgIcon]="node.icon || 'heroicons_outline:folder'"></mat-icon>

                <!-- Label -->
                <span class="truncate font-medium">{{ node.label }}</span>

                <!-- Spacer -->
                <span class="flex-auto"></span>

                <!-- List icon (visible on hover) — for top-level folders -->
                @if (node.id === 'my-clients' || node.id === 'my-products' || node.id === 'my-projects') {
                    <mat-icon class="icon-size-3.5 mr-1 flex-shrink-0 text-gray-500 opacity-0 group-hover:opacity-100"
                        [svgIcon]="'heroicons_outline:list-bullet'"
                        [matTooltip]="node.id === 'my-clients' ? t('projects.navTree.allClients') : node.id === 'my-products' ? t('projects.navTree.allProducts') : t('projects.navTree.allProjects')"
                        (click)="$event.stopPropagation(); navigateToList(node.id)">
                    </mat-icon>
                }

                <!-- Edit icon (visible on hover) — before badge -->
                @if (node.entityId) {
                    <mat-icon class="icon-size-3.5 mr-1 flex-shrink-0 text-gray-500 opacity-0 group-hover:opacity-100"
                        [svgIcon]="'heroicons_outline:pencil-square'"
                        (click)="$event.stopPropagation(); navigateToEntity(node)">
                    </mat-icon>
                }

                <!-- Count badge — after edit icon -->
                @if (shouldShowBadge(node)) {
                    <span class="ml-1 flex-shrink-0 rounded-full px-1.5 py-0.5 text-xs font-medium"
                        [ngClass]="hasUnseenChildren(node)
                            ? 'bg-primary text-on-primary'
                            : 'bg-white/10 text-gray-400'">
                        {{ getItemCount(node) }}
                    </span>
                }
            </div>

            <!-- Children (collapsible) -->
            @if (node.expanded && node.children?.length) {
                @for (child of node.children; track child.id) {
                    <ng-container
                        *ngTemplateOutlet="treeNode; context: { $implicit: child, siblings: node.children }"
                    ></ng-container>
                }
            }
        </div>
    }
</ng-template>
</ng-container>
